const post = {
  slug: "which-checkout-dm-plan-should-you-choose",
  title: "Which Checkout DM Plan Should You Choose? A Simple Guide",
  seoTitle: "Which Checkout DM Plan Should You Choose?",
  description:
    "Not sure which Checkout DM plan fits? Match Free, Creator, Starter, Growth or Pro to the way you sell, using the limits and prices that actually decide it.",
  excerpt:
    "Five plans can look like a lot. Answer three questions about what you sell, how many customers you talk to and what you want automated, and the right plan usually picks itself.",
  category: "Product",
  tags: ["Checkout DM pricing", "Which Checkout DM plan", "Checkout DM plans for Instagram sellers"],
  date: "2026-10-29T12:00:00Z",
  cover: {
    src: "/blog-images/which-checkout-dm-plan-should-you-choose-cover.jpg",
    alt: "A phone showing Checkout DM plans matched to how a seller sells, from Free to Pro",
  },
  blocks: [
    {
      type: "p",
      text:
        "Checkout DM has five plans, and the right one is rarely about how big your shop is. It comes down to three practical things: what you sell, how many customers message you each month and which jobs you want the tool to do for you. This guide helps you answer those questions and land on one plan. For a full list of every feature, see [Checkout DM pricing and plans explained](/blog/checkout-dm-pricing-and-plans-explained).",
    },
    {
      type: "p",
      text:
        "One thing is the same on every plan: Checkout DM takes **no commission and no transaction fee** on your orders, and payments go to your own payment account. So you are choosing a plan for what it includes, not for what it takes from each sale.",
    },

    { type: "h2", text: "Start with three questions" },
    {
      type: "ol",
      items: [
        "**Do you sell digital products, physical products or both?** The Creator plan is digital-only. If you ship physical goods, Free, Starter, Growth and Pro are the plans that allow it.",
        "**How many customers message you in a month?** Free handles 15 unique customer conversations per month. Every paid plan has unlimited conversations.",
        "**Which jobs do you want automated?** Comment-to-DM starts on Creator. Delivery tracking and automatic stock updates start on Starter. Coupons, offers and automatic abandoned-cart recovery start on Growth. Team seats start on Pro.",
      ],
    },

    { type: "h2", text: "The quick match" },
    {
      type: "table",
      headers: ["If this sounds like you", "Plan", "Monthly price"],
      rows: [
        ["You want to test DM selling with real customers", "Free", "\u20B90 ($0)"],
        ["You sell digital files or affiliate links only", "Creator", "\u20B9499 ($9)"],
        ["You ship physical products and talk to customers daily", "Starter", "\u20B91,499 ($19)"],
        ["You want coupons, offers and automatic cart recovery", "Growth", "\u20B92,899 ($39)"],
        ["You have a large catalog or a team", "Pro", "\u20B96,499 ($69)"],
      ],
    },
    {
      type: "callout",
      tone: "peach",
      title: "Check the live prices",
      text:
        "These are the prices on the [Pricing page](/pricing) at the time of writing. If they differ, the Pricing page is the final word.",
    },

    { type: "h2", text: "Free: you want to test before you commit" },
    {
      type: "p",
      text:
        "Choose Free if you are not sure DM selling suits your shop yet. You can list up to **5 products** and handle **15 unique customer conversations a month**, which is enough to run a few real orders from start to finish. Checkout DM branding is shown to your customers in DMs, checkout and emails. To see exactly what you get and where it stops, read [the Free plan guide](/blog/checkout-dm-free-plan-limits-and-when-to-upgrade).",
    },

    { type: "h2", text: "Creator: you sell digital products or affiliate links" },
    {
      type: "p",
      text:
        "Creator is built for people who sell files, courses, templates or affiliate products. It allows up to **150 digital products** and unlimited conversations, and it adds Comment-to-DM automation, free digital products you can give away, a Preview or Buy button for affiliate links and a public Storefront with one-link checkout. It does not allow physical products, so if you ship anything, skip it. Our guide to [selling digital products on Instagram](/blog/sell-digital-products-on-instagram) shows the flow from comment to download.",
    },

    { type: "h2", text: "Starter: you ship physical products every week" },
    {
      type: "p",
      text:
        "Starter is the first plan for physical goods at scale. It raises the limit to **500 products**, removes Checkout DM branding everywhere and removes the conversation limit. It also adds the jobs that eat a seller\u2019s evening:",
    },
    {
      type: "ul",
      items: [
        "Automatic delivery status updates (Delivered, RTO, Failed) when your courier is connected.",
        "Automatic stock updates on every sale, return or exchange.",
        "Automatic free shipping above a minimum cart value.",
        "Reminders for unshipped orders and abandoned-cart information, so you can follow up yourself.",
      ],
    },
    {
      type: "p",
      text:
        "If returns to origin are your biggest worry, read [how to reduce RTO as an Instagram seller](/blog/reduce-rto-instagram-sellers-india).",
    },

    { type: "h2", text: "Growth: you want offers and recovery on autopilot" },
    {
      type: "p",
      text:
        "Growth raises the catalog to **1,000 products** and is about getting more from the traffic you already have. You get Story replies matched to the exact product, automatic and manual coupons, time-based and count-based offers, a multi-step automatic abandoned-cart recovery sequence, low-stock alerts per size, review requests after a number of days you choose, Dead Stock Insights, CSV export, revenue and conversion analytics and priority support. If you plan to run [discount codes and free shipping offers](/blog/discount-codes-and-free-shipping-for-instagram-orders), coupons and offers start here.",
    },

    { type: "h2", text: "Pro: a large catalog or a team" },
    {
      type: "p",
      text:
        "Pro includes everything in Growth with up to **10,000 products** and three team members: one admin and two users, with extra seats available. It adds Google-indexable product pages, bulk photo upload of up to 50 images at once, quantity-based discounts, cross-sell suggestions in order and shipped emails, real-time webhooks for orders and returns, advanced analytics and dedicated support. Pro also includes a one-to-one onboarding call with the founder. For the team side, see [how to add team members](/blog/add-team-members-to-checkout-dm).",
    },

    { type: "h2", text: "Signs you have outgrown your plan" },
    {
      type: "ul",
      items: [
        "**You are reaching the conversation limit.** On Free, new customer conversations pause once you hit the monthly limit, until it resets or you upgrade.",
        "**You want to list a sixth product.** Free stops at 5 products. For physical products, Starter is the next step.",
        "**You chase the same follow-ups every day.** If you message abandoned carts by hand, Growth automates it.",
        "**Someone else needs access.** Team members start on Pro.",
      ],
    },

    { type: "h2", text: "Do not overthink the first choice" },
    {
      type: "p",
      text:
        "You can move between plans at any time without losing your products, orders or customer history, and there is no long-term contract. Yearly billing gives you two months free, because you pay for ten months instead of twelve. If you are unsure, start on the lowest plan that includes the one feature you cannot do without, and upgrade when a limit starts to hurt.",
    },
    {
      type: "p",
      text:
        "You can cancel from your dashboard and keep using your current plan until the period you have already paid for ends. Subscription payments are non-refundable, including partial or unused periods, so monthly billing is the safer way to begin until you are sure.",
    },
    {
      type: "p",
      text:
        "If you are still comparing tools rather than plans, our [honest comparison of free Instagram automation tools](/blog/best-free-instagram-automation-tools) is a good next read. To see the full cost picture, including payment gateway fees, read [what Checkout DM really costs](/blog/what-checkout-dm-really-costs-plan-gateway-fees).",
    },
  ],
  related: [
    { to: "/pricing", title: "Checkout DM pricing", description: "Current plans, prices and the full feature comparison." },
    { to: "/faq", title: "Frequently Asked Questions", description: "Answers on plans, payments, shipping and returns." },
    { to: "/help", title: "Help Guide", description: "How to set up and run your Checkout DM account." },
  ],
  faqs: [
    {
      q: "Which Checkout DM plan is best for a beginner?",
      a: "If you are not sure DM selling suits your shop yet, start on Free, which allows 5 products and 15 unique customer conversations a month. If you only sell digital products or affiliate links, Creator is built for that.",
    },
    {
      q: "Can I sell physical products on the Creator plan?",
      a: "No. Creator is digital-only. Free, Starter, Growth and Pro can sell both physical and digital products.",
    },
    {
      q: "When should I upgrade from the Free plan?",
      a: "Upgrade when you reach the 5-product or 15-conversation limit, or when you need a feature that starts on a paid plan, such as Comment-to-DM from Creator, delivery tracking from Starter or coupons from Growth.",
    },
    {
      q: "Can I switch plans later?",
      a: "Yes, at any time. You can move between plans without losing your products, orders or customer history, and there is no long-term contract.",
    },
    {
      q: "Does any Checkout DM plan charge a commission on sales?",
      a: "No. There is no commission or transaction fee on any plan, and payments go directly to your own payment account.",
    },
  ],
};

export default post;
